/**
 * Custom Dashboard Widget
 * - compatible with smartsuite-dashboard-app version ^4.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import last from 'lodash/last';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { DateTimeFormats } from 'brock-date-time';
import {
    Table,
    TableBody,
    TableCell,
    TableHeaderCell,
    TableHeaderCellSortable,
    TableRow,
} from 'smartsuite-ui';
import useSortable from '../../../hooks/useSortable';
import renderTypes from '../../renderTypes';
import AdvancedSearchLinkCell from './AdvancedSearchLinkCell';

function AdvancedSearchLinkTable({
    configuration,
    data,
    timeAndPrecision,
    filterValues,
    whereClauses,
    legendFormatter,
    formatValue,
}) {
    const [sortObject, setSort, sortedResults] = useSortable(
        data,
        configuration.initialSortObject
    );

    const renderHeaderColumn = (columnConfig, columnIndex) => {
        const { colspan, dataKey, renderType } = columnConfig;
        const scope = colspan > 1 ? 'colgroup' : 'col';

        if (renderType === renderTypes.placeholder) {
            return <TableHeaderCell colSpan={colspan} scope={scope} />;
        }

        if (renderType === renderTypes.staticParentHeader) {
            return (
                <TableHeaderCell colSpan={colspan} scope={scope}>
                    {legendFormatter(dataKey, null, columnIndex)}
                </TableHeaderCell>
            );
        }

        return (
            <TableHeaderCellSortable
                key={dataKey}
                columnName={dataKey}
                sortObject={sortObject}
                onSort={setSort}
            >
                {legendFormatter(dataKey, null, columnIndex)}
            </TableHeaderCellSortable>
        );
    };

    const renderHeaderRow = (headerRow) => (
        <TableRow>{headerRow.map(renderHeaderColumn)}</TableRow>
    );

    /* eslint-disable complexity */
    const renderCell = (resultRow, columnConfig, cellKey) => {
        switch (columnConfig.renderType) {
            case renderTypes.advancedSearchLink: {
                return (
                    <AdvancedSearchLinkCell
                        key={cellKey}
                        argumentConfigs={columnConfig.advancedSearchArguments}
                        dataRow={resultRow}
                        filterValues={filterValues}
                        pathName={columnConfig.pathName}
                        timeAndPrecision={timeAndPrecision}
                        whereClauses={whereClauses}
                        value={formatValue(resultRow[columnConfig.dataKey])}
                    />
                );
            }
            case renderTypes.dateTime: {
                const dateTimeFormat =
                    columnConfig.dateTimeFormat || DateTimeFormats.DATETIME;
                return (
                    <TableCell key={cellKey}>
                        {moment(resultRow[columnConfig.dataKey]).format(
                            dateTimeFormat
                        )}
                    </TableCell>
                );
            }

            case renderTypes.text:
            default: {
                return (
                    <TableCell key={cellKey}>
                        {formatValue(resultRow[columnConfig.dataKey])}
                    </TableCell>
                );
            }
        }
    };
    /* eslint-enable complexity */

    const renderDataRow = (resultRow) => {
        const columnConfigs = last(configuration.columns);
        const rowKey = JSON.stringify(resultRow);

        return (
            <TableRow key={rowKey}>
                {columnConfigs.map((columnConfig) => {
                    const cellKey = `${rowKey};${columnConfig.dataKey}`;
                    return renderCell(resultRow, columnConfig, cellKey);
                })}
            </TableRow>
        );
    };

    return (
        <div className="widget__table-container">
            <Table>
                <thead>{configuration.columns.map(renderHeaderRow)}</thead>
                <TableBody>{sortedResults.map(renderDataRow)}</TableBody>
            </Table>
        </div>
    );
}

AdvancedSearchLinkTable.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    data: PropTypes.instanceOf(Array).isRequired,
    formatValue: PropTypes.func.isRequired,
    timeAndPrecision: PropTypes.instanceOf(Object),
    filterValues: PropTypes.instanceOf(Object),
    whereClauses: PropTypes.instanceOf(Array),
    legendFormatter: PropTypes.func.isRequired,
};

AdvancedSearchLinkTable.defaultProps = {
    timeAndPrecision: {},
    filterValues: {},
    whereClauses: [],
};

export default React.memo(
    AdvancedSearchLinkTable,
    (prevProps, nextProps) =>
        isEqual(prevProps.data, nextProps.data) &&
        isEqual(prevProps.timeAndPrecision, nextProps.timeAndPrecision) &&
        isEqual(prevProps.filterValues, nextProps.filterValues) &&
        isEqual(prevProps.whereClauses, nextProps.whereClauses)
);
